import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { GoHome, GoHomeFill } from 'react-icons/go';
import { BsCalendar3, BsCalendar3Fill, BsCalendarPlus } from 'react-icons/bs';
import { HiOutlineCube, HiCube } from 'react-icons/hi2';
import { HiOutlineClipboardList, HiClipboardList } from 'react-icons/hi';
import { HiOutlineUser, HiUser } from 'react-icons/hi2';
import { FiCalendar, FiPlusCircle } from 'react-icons/fi';
import useRole from '../../hooks/useRole';

const spinOnce = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const CalendarSpin = styled.span`
  display: inline-flex;
  animation: ${spinOnce} 0.8s ease-out;
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(8px) scale(0.95); }
  to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
`;

const NavContainer = styled.nav`
  flex-shrink: 0;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  height: 64px;
  background: #FFFFFF;
  border-top: 1px solid #E8E8E8;
  display: flex;
  align-items: flex-start;
  padding-top: 10px;
  justify-content: space-around;
  z-index: 100;
  padding-bottom: calc(env(safe-area-inset-bottom) + 8px);
`;

const NavItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: ${({ $active }) => $active ? '#4A6741' : '#8C9AA4'};
  transition: color 0.2s;
`;

const NavLabel = styled.span`
  font-size: 11px;
  font-weight: ${({ $active }) => $active ? '600' : '500'};
`;

const CenterWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const CenterBtn = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #FFFFFF;
  color: ${({ $active }) => $active ? '#4A6741' : '#2C3640'};
  border: 2px solid #E8E8E8;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -32px;
  cursor: pointer;
  transition: transform 0.2s, background 0.2s;
  transform: rotate(${({ $open }) => ($open ? '45deg' : '0deg')});

  &:active {
    transform: rotate(${({ $open }) => ($open ? '45deg' : '0deg')}) scale(0.93);
  }
`;

const CenterLabel = styled.span`
  font-size: 11px;
  font-weight: ${({ $active }) => $active ? '600' : '500'};
  color: ${({ $active }) => $active ? '#2C3640' : '#8C9AA4'};
  margin-top: 2px;
`;

const MenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 99;
`;

const MenuPopup = styled.div`
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18);
  z-index: 100;
  overflow: hidden;
  min-width: 220px;
  animation: ${popIn} 0.15s ease-out;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;

  &:active { background: #f5f5f5; }
  & + & { border-top: 1px solid #f0f0f0; }
`;

const MenuIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${({ $bg }) => $bg || '#f0f0f0'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MenuTexts = styled.div`
  flex: 1;
`;

const MenuLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #222;
`;

const MenuDesc = styled.div`
  font-size: 11px;
  color: #999;
  margin-top: 1px;
`;

const NAV_ITEMS = [
  { path: '/home', label: '홈', icon: GoHome, activeIcon: GoHomeFill },
  { path: '/products', label: '자재상품', icon: HiOutlineCube, activeIcon: HiCube },
  { path: '/orders', label: '주문내역', icon: HiOutlineClipboardList, activeIcon: HiClipboardList },
  { path: '/mypage', label: '마이', icon: HiOutlineUser, activeIcon: HiUser },
];

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isGeneral } = useRole();
  const [menuOpen, setMenuOpen] = useState(false);
  const isScheduleActive = location.pathname.startsWith('/schedule');

  const handleMenuAction = (path, state) => {
    setMenuOpen(false);
    navigate(path, state ? { state } : undefined);
  };

  return (
    <>
      {menuOpen && <MenuBackdrop onClick={() => setMenuOpen(false)} />}
      <NavContainer>
        {/* 왼쪽 2개 */}
        {NAV_ITEMS.slice(0, 2).map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          const Icon = isActive ? item.activeIcon : item.icon;
          return (
            <NavItem key={item.path} onClick={() => navigate(item.path)} $active={isActive}>
              <Icon size={24} />
              <NavLabel $active={isActive}>{item.label}</NavLabel>
            </NavItem>
          );
        })}

        {/* 가운데 일정 버튼 (일반 역할은 숨김) */}
        {!isGeneral && (
          <CenterWrap>
            <CenterBtn $open={menuOpen} $active={isScheduleActive} onClick={() => setMenuOpen(o => !o)}>
              <CalendarSpin><BsCalendar3 size={24} /></CalendarSpin>
            </CenterBtn>
            {menuOpen && (
              <MenuPopup>
                <MenuItem onClick={() => handleMenuAction('/schedule')}>
                  <MenuIcon $bg="#E8F5E9"><FiCalendar size={17} color="#4A6741" /></MenuIcon>
                  <MenuTexts>
                    <MenuLabel>일정보기</MenuLabel>
                    <MenuDesc>등록된 일정 확인하기</MenuDesc>
                  </MenuTexts>
                </MenuItem>
                <MenuItem onClick={() => handleMenuAction('/schedule', { openForm: true })}>
                  <MenuIcon $bg="#FFF3E0"><FiPlusCircle size={17} color="#E85830" /></MenuIcon>
                  <MenuTexts>
                    <MenuLabel>일정등록</MenuLabel>
                    <MenuDesc>새 일정 등록하기</MenuDesc>
                  </MenuTexts>
                </MenuItem>
              </MenuPopup>
            )}
            <CenterLabel $active={isScheduleActive}>일정</CenterLabel>
          </CenterWrap>
        )}

        {/* 오른쪽 2개 */}
        {NAV_ITEMS.slice(2, 4).map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          const Icon = isActive ? item.activeIcon : item.icon;
          return (
            <NavItem key={item.path} onClick={() => navigate(item.path)} $active={isActive}>
              <Icon size={24} />
              <NavLabel $active={isActive}>{item.label}</NavLabel>
            </NavItem>
          );
        })}
      </NavContainer>
    </>
  );
};

export default BottomNav;
